import React, { memo, forwardRef } from 'react';
import { useLanguage } from '../../contexts';

/**
 * Input - A styled form input component
 * 
 * @example
 * <Input 
 *   label="סיסמה" 
 *   type="password" 
 *   value={password} 
 *   onChange={(e) => setPassword(e.target.value)}
 *   error="סיסמה שגויה"
 * />
 */

export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Input label */
  label?: string;
  /** Error message */
  error?: string;
  /** Helper text below input */
  helperText?: string;
  /** Size preset */
  size?: InputSize;
  /** Whether to center the text */
  centered?: boolean;
  /** Icon element to display (positioned based on RTL) */
  icon?: React.ReactNode;
  /** Right-side action element (e.g., show/hide password button) */
  endAction?: React.ReactNode;
}

const sizeStyles: Record<InputSize, string> = {
  sm: 'p-2 sm:p-3 text-base sm:text-lg rounded-lg sm:rounded-xl border-2',
  md: 'p-3 sm:p-4 text-lg sm:text-xl rounded-xl sm:rounded-2xl border-4',
  lg: 'p-4 sm:p-5 text-xl sm:text-2xl rounded-xl sm:rounded-2xl border-4',
};

export const Input = memo(forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  size = 'md',
  centered = false,
  icon,
  endAction,
  className = '',
  disabled,
  ...props
}, ref) => {
  const { isRTL } = useLanguage();
  
  return (
    <div className="w-full">
      {label && (
        <label className="block text-body font-bold text-gray-700 dark:text-dark-text mb-2">
          {label}
        </label>
      )}
      
      <div className="relative">
        {icon && (
          <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-3 sm:right-4' : 'left-3 sm:left-4'} text-gray-400 dark:text-dark-muted`}>
            {icon}
          </div>
        )}
        
        <input
          ref={ref}
          disabled={disabled}
          className={`
            w-full
            font-bold font-heebo
            bg-white dark:bg-dark-surface
            text-gray-800 dark:text-dark-text
            border-gray-200 dark:border-dark-border
            focus:border-green-400 dark:focus:border-green-500
            focus:outline-none
            transition-colors duration-200
            placeholder:text-gray-400 dark:placeholder:text-dark-muted
            disabled:opacity-50 disabled:cursor-not-allowed
            ${centered ? 'text-center' : ''}
            ${icon ? (isRTL ? 'pr-12' : 'pl-12') : ''}
            ${endAction ? (isRTL ? 'pl-12' : 'pr-12') : ''}
            ${error ? 'border-red-400 dark:border-red-500 focus:border-red-400 dark:focus:border-red-500' : ''}
            ${sizeStyles[size]}
            ${className}
          `}
          {...props}
        />
        
        {endAction && (
          <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3 sm:left-4' : 'right-3 sm:right-4'}`}>
            {endAction}
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-2 text-sm font-bold text-red-500 animate-pulse">
          {error}
        </p>
      )}
      
      {helperText && !error && (
        <p className="mt-2 text-caption">
          {helperText}
        </p>
      )}
    </div>
  );
}));

Input.displayName = 'Input';
